<script setup>
import ShortlistJobBtn from 'components/btn/ShortlistJobBtn.vue';
import CompanyAvatar from 'components/CompanyAvatar.vue';
import GenericChip from 'components/GenericChip.vue';
import JobAvatar from 'components/JobAvatar.vue';
import JobHeaderInfo from 'components/JobHeaderInfo.vue';
import LocationChip from 'components/LocationChip.vue';
import AppRoute from 'src/router/app-route';
import { Route } from 'src/router/routes';
import { formattedRemuneration } from 'src/utils/job-utils';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { getRelativeTime } from 'utils/date-utils';

const $emit = defineEmits(['click']);

const $props = defineProps({
    job: {
        type: Object,
        required: true
    },
    inPreviewContext: {
        type: Boolean,
        default: false
    },
    minimal: {
        type: Boolean,
        default: false
    },
    dense: {
        type: Boolean,
        default: false
    },
    active: {
        type: Boolean,
        default: false
    },
    hideShortlistBtn: {
        type: Boolean,
        default: false
    },
    openSeparateTab: {
        type: Boolean,
        default: false
    }
});

const $router = useRouter();

/** @type {import('vue').Ref<HTMLElement|null>} */
const jobListItemElement = ref(null);

const formattedRemunerationRange = computed(() => formattedRemuneration(
    $props.job.scopes[0].location.currencyCode,
    $props.job.scopes[0].remuneration.range[0],
    $props.job.scopes[0].remuneration.range[1],
    $props.job.scopes[0].remuneration.cycle
));

const expiryMessage = computed(() => {
    if (!$props.job.expiresAt || $props.job.status === 'Expired') {
        return null;
    }
    const expiryDate = new Date($props.job.expiresAt);
    const now = new Date();
    const diffMs = expiryDate - now;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffDays <= 7 && diffDays > 0) {
        return `Expires in ${diffDays} day${diffDays === 1 ? '' : 's'}`;
    } else if (diffHours > 0 && diffDays === 0) {
        return `Expires in ${diffHours} hour${diffHours === 1 ? '' : 's'}`;
    } else if (diffHours <= 0 && diffMs > 0) {
        return 'Expires shortly!';
    } else {
        return null;
    }
});

const resultPageRouteName = computed(() => {
    const parentRoute = getPredefinedParentRoute($router.currentRoute.value);
    switch (parentRoute?.name) {
        case Route.SearchResultsPage:
            return Route.JobResultPage;
        case Route.ShortlistPage:
            return Route.ShortlistResultPage;
        default:
            return Route.JobPage;
    }
});

function getPredefinedParentRoute(route) {
    return [...route.matched]
        .reverse()
        .find(r => [Route.SearchResultsPage, Route.ShortlistPage].includes(r.name));
}

function getJobPageUrl() {
    if ($props.inPreviewContext) {
        return null;
    }
    return AppRoute.build(Route.JobPage, { jobScopeId: $props.job.scopes[0].id }).url;
}

async function gotoJobResultPage() {
    if ($props.inPreviewContext) {
        return;
    }
    const destinationRoute = AppRoute.build(resultPageRouteName.value, { jobScopeId: $props.job.scopes[0].id });
    if ($props.openSeparateTab) {
        window.open(destinationRoute.getResolvedUrl($router), '_blank');
    } else {
        await $router.push(AppRoute.build(resultPageRouteName.value, { jobScopeId: $props.job.scopes[0].id }));
    }
    $emit('click', jobListItemElement.value);
}
</script>

<template>
    <q-card
        ref="jobListItemElement"
        :class="[ 'job-list-item', { 'clickable cursor-pointer': !inPreviewContext && job.status !== 'Expired', 'active': active, 'dense': dense } ]"
        rounded
        bordered
        flat
    >
        <a
            v-if="job.status !== 'Expired'"
            @click.prevent="gotoJobResultPage"
            :href="getJobPageUrl(job)"
            :aria-label="`View the ${job.title} Job at ${job.company.name}`"
            class="job-link"
        >
            <span class="invisible">View job</span>
        </a>
        <div v-if="job.status === 'Expired'" class="expired-overlay"></div>
        <q-card-section class="q-pa-lg">
            <div :class="['job-list-item-container flex', dense ? 'column' : 'row-md']">
                <div class="col">
                    <div class="row q-col-gutter-x-md flex items-center no-wrap">
                        <JobHeaderInfo :job :minimal :dense/>
                        <div v-if="!minimal && !inPreviewContext && !hideShortlistBtn" class="self-start q-ml-auto q-mr-none">
                            <ShortlistJobBtn :job-scope="job.scopes[0]" class="z-top"/>
                        </div>
                        <div v-else-if="minimal" class="self-start">
                            <JobAvatar v-if="job.logoUrl" :job size="66px" class="rounded-borders" hide-fallback/>
                            <CompanyAvatar v-else :company="job.company" size="66px" class="rounded-borders" hide-fallback/>
                        </div>
                    </div>
                    <div class="flex row q-mb-none q-mt-md no-wrap">
                        <div class="q-gutter-x-xs">
                            <LocationChip :location="job.scopes[0].location"/>
                            <GenericChip :label="formattedRemunerationRange"/>
                            <GenericChip :label="job.employmentType"/>
                            <GenericChip :label="job.scopes[0].workLocation"/>
                        </div>
                        <div v-if="minimal && !inPreviewContext && !hideShortlistBtn" class="self-end q-ml-auto q-mr-none">
                            <ShortlistJobBtn :job-scope="job.scopes[0]" class="z-top"/>
                        </div>
                    </div>
                    <template v-if="!minimal && job.highlights && job.highlights.length > 0">
                        <ul class="q-mt-md q-mb-none q-pl-md">
                            <li v-for="(highlight, i) of job.highlights" :key="i">{{ highlight }}</li>
                        </ul>
                    </template>
                    <template v-if="!minimal && job.summary">
                        <p class="q-mb-none q-mt-md">{{ job.summary }}</p>
                    </template>
                    <template v-if="job.appliedAt">
                        <div class="flex row items-center q-col-gutter-x-sm q-mt-lg text-grey-8">
                            <q-icon name="sym_r_assignment_late" size="sm"/>
                            <p class="text-overline text-uppercase q-mb-none q-mt-none">Applied {{ getRelativeTime(job.appliedAt) }}.</p>
                        </div>
                    </template>
                    <template v-else-if="expiryMessage && job.status !== 'Expired'">
                        <div class="flex row items-center q-col-gutter-x-sm q-mt-lg">
                            <q-icon name="sym_r_timer" size="sm" class="text-negative"/>
                            <p class="text-overline text-uppercase q-mb-none q-mt-none text-negative text-weight-bold">{{ expiryMessage }}</p>
                        </div>
                    </template>
                    <template v-else-if="job.status === 'Expired'">
                        <div class="flex row items-center q-col-gutter-x-sm q-mt-lg text-grey-8">
                            <q-icon name="sym_r_event_busy" size="sm"/>
                            <p class="text-overline text-uppercase q-mb-none q-mt-none">No longer available.</p>
                        </div>
                    </template>
                </div>
            </div>
        </q-card-section>
    </q-card>
</template>

<style scoped lang="scss">
.job-list-item {
    position: relative;
    scroll-margin: 16px !important;
    transition: all 300ms ease-in-out, background-color 0ms, color 0ms;
    width: 100%;

    .body--dark & {
        background: rgba($white, 0.07);
    }

    .expired-overlay {
        background: rgba($grey-3, 0.5);
        backdrop-filter: grayscale(1);
        height: 100%;
        left: 0;
        position: absolute;
        top: 0;
        width: 100%;
        z-index: 99;
    }

    .job-list-item-container {
        flex-wrap: nowrap;
    }

    .job-link {
        height: 100%;
        position: absolute;
        width: 100%;
    }
}

.z-top {
    z-index: 99 !important;
}
</style>
